import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StoreProvider } from '../../contexts/StoreContext';
import { StoreDataProvider } from '../../contexts/StoreDataContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { ChatProvider } from '../../contexts/ChatContext';
import { isAppShell } from '../../marketing/utils/isInstalledApp';
import { isMarketingPath } from '../../marketing/utils/marketingPaths';
import { isOAuthCallbackPath } from '../../utils/oauthCallback';
import { useWidgetSnapshotSync } from '../../hooks/useWidgetSnapshotSync';
import { FCMProvider } from '../FCMProvider/FCMProvider';
import { AssistantDockProvider } from '../AssistantDock/assistantDockContext';
import AssistantDock from '../AssistantDock/AssistantDock';

function isSuperAdminZone(pathname: string): boolean {
  if (pathname === '/super-admin') return true;
  return pathname.startsWith('/super-admin/');
}

function isPublicLegalPage(pathname: string): boolean {
  return pathname === '/privacy' || pathname === '/privacy.html'
    || pathname === '/terms' || pathname === '/terms.html'
    || pathname === '/data-deletion' || pathname === '/data-deletion.html';
}

/**
 * Hors zone super-admin : boutique, FCM, chat, etc.
 * Sur /super-admin/* : pas de StoreData ni FCM pour éviter conflits et blocages quand une session marchande est aussi active.
 * Pages légales publiques : pas de session boutique non plus.
 */
export function AppRoutesShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  useWidgetSnapshotSync();

  if (isSuperAdminZone(pathname) || isPublicLegalPage(pathname) || isOAuthCallbackPath(pathname)) {
    return <ToastProvider>{children}</ToastProvider>;
  }

  const rootNeedsStore = pathname === '/' && isAppShell() && (isLoading || isAuthenticated);
  if (isMarketingPath(pathname) && !rootNeedsStore) {
    return <ToastProvider>{children}</ToastProvider>;
  }

  return (
    <StoreProvider>
      <StoreDataProvider>
        <ToastProvider>
          <ChatProvider>
            <AssistantDockProvider>
              <FCMProvider>{children}</FCMProvider>
              <AssistantDock />
            </AssistantDockProvider>
          </ChatProvider>
        </ToastProvider>
      </StoreDataProvider>
    </StoreProvider>
  );
}
